import { Box, Grid, Tooltip } from "@mui/material";
import PureModal from "components/PureModal";
import React, { useCallback, useEffect, useRef, useState } from "react";
import _, { chain, find, set } from "lodash";
import {
  StyledCountText,
  StyledImportCard,
  StyledImportIcon,
  StyledSearchBar,
} from "./FilterModal.styles";
import Dropzone from "react-dropzone";
import Button, { IconButton } from "@metacrm/metacrm-material-ui/dist/Button";
import InputField from "@metacrm/metacrm-material-ui/dist/InputField";
import AutoCompleteDropdown from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import Papa from "papaparse";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import axios from "axios";
import { apiUrl } from "features/configure";
import { useOutletContext } from "react-router";
import {
  getHexImg,
  getShortAddress,
  isAddress,
  getEcosystemByAddress,
  getChainList,
} from "features/helpers/utils";
import DatePicker from "@metacrm/metacrm-material-ui/dist/DatePicker";
import { selectEnabledEcosystems } from "features/metadesk/redux/entityConfig/entityConfig.selector";
import { useSelector } from "react-redux";
import moment from "moment";
import LoadingComponent from "components/LoadingComponent/LoadingComponent";
import WebhookImport from "./WebhookImport";
import UserTagComponent from "components/UserTag/UserTag";
import { useIntl } from "react-intl";
import {
  useForm,
  FormProvider,
  Controller,
  useFieldArray,
} from "react-hook-form";
import { useTheme } from "@emotion/react";
import walletDynamic from "assets/img/wallet-dynamic.svg";
import walletPrivy from "assets/img/wallet-privy.svg";

const defaultValues = {
  clientUid: "",
  address: "",
  chainId: null,
  importType: null,
  lastEngagement: [null, null],
  contract: null,
  csv: "",
  csvName: "",
  totalCount: 0,
  createdCount: 0,
  updatedCount: 0,
  tags: [],
  walletAddress: "",
  email: "",
  discordHandle: "",
  telegramHandle: "",
  twitterHandle: "",
};

export default function ImportUserModal({
  open,
  handleClose,
  closeBtn = false,
  getUserList,
  ...props
}) {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const methods = useForm({
    defaultValues,
    mode: "onSubmit",
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    setError,
    formState,
  } = methods;
  const { isDirty } = formState;
  const values = watch();

  // step => init , csvImport , smartContractImport, singleUserImport
  const [step, setStep] = useState("init");
  const [entityName] = useOutletContext();
  const [isUploading, setIsUploading] = useState(false);
  const dispatchSnackbar = useDispatchSnackbar();
  const evmChainList = getChainList("evm");
  const enabledEcosystems = useSelector(selectEnabledEcosystems);

  const fetchFromSmartContractDisabled = !enabledEcosystems.includes("evm");

  const Header = () => {
    if (step === "init") return formatMessage({ id: "popup.cdpImport.title" });
    return (
      <>
        <i
          className="meta-crm-icon-ic_arrow-left font-size-24"
          onClick={() => {
            if (isUploading) return;
            setStep("init");
          }}
          style={{ cursor: isUploading ? "not-allowed" : "pointer" }}
        />
        <Box ml="4px">
          {step === "webhook"
            ? formatMessage({ id: "import.cdpImport.header.webhook" })
            : formatMessage({ id: "import.cdpImport.header.importCustomers" })}
        </Box>
        <Box color="#777" fontSize={20} fontWeight={400} ml="4px">
          {step === "csvImport" && "Upload CSV"}
          {step === "smartContractImport1" &&
            `${formatMessage({
              id: "global.importCustomers.smartContract",
            })} (1/2)`}
          {step === "smartContractImport2" &&
            `${formatMessage({
              id: "global.importCustomers.smartContract",
            })} (2/2)`}
          {step === "singleUserImport" &&
            `${formatMessage({ id: "global.import.addSingleUser" })}`}
        </Box>
      </>
    );
  };

  const handleChooseFile = async (f) => {
    // setIsUploading(true);
    // checkFile(f);
    if (!f.name.endsWith(".csv")) {
      return dispatchSnackbar({ message: "Not supported file type!" });
    }
    if (f.size > 30 * 1024 * 1024) {
      return dispatchSnackbar({ message: "Over file limit 30MB!" });
    }

    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: async function () {
        try {
          setStep("csvImport");
          await uploadToServer(f);
        } catch (err) {
          setStep("init");
          dispatchSnackbar({ err });
          reset({
            ...defaultValues,
          });
        } finally {
          setIsUploading(false);
        }
      },
    });
  };

  function handleCancel() {
    if (step === "smartContractImport2") {
      setStep("smartContractImport1");
    } else {
      reset({
        ...defaultValues,
      });
      setOpenAddressSearch(false);
      setImportContract({});
      setStep("init");
    }
  }

  const uploadToServer = async (f) => {
    if (!f) return;
    setIsUploading(true);
    let postFormData = new FormData();
    postFormData.append("csv", f);
    postFormData.append("entityName", entityName);
    const uploadResult = await axios.post(
      `${apiUrl}/api/gcp/uploadCSV?entityName=` + entityName,
      postFormData
    );
    let csv = _.get(uploadResult, "data.csv[0]");
    const previewResult = await axios.post(
      `${apiUrl}/api/userSource/${entityName}/csv/preview`,
      { csvFileUrl: csv }
    );

    setValue("csv", csv);
    setValue("csvName", _.get(f, "name"));
    setValue("totalCount", previewResult.data?.totalCount);
    setValue("createdCount", previewResult.data?.createdCount);
    setValue("updatedCount", previewResult.data?.updatedCount);
    setValue("validation", previewResult.data?.validation);
    setValue("tags", []);
    setIsUploading(false);
  };

  const handleImport = async () => {
    try {
      if (step === "smartContractImport1") {
        const isValid = await trigger([
          "chainId",
          "address",
          "importType",
          "lastEngagement",
        ]);
        if (!isValid) return;

        return setStep("smartContractImport2");
      }

      const tags = values.tags?.map((t) => t._id) || [];
      setIsUploading(true);
      let payload;

      if (step === "smartContractImport2") {
        const data = {
          address: values.address,
          chainId: values.chainId.value,
          type: values.importType.value,
          timeRange: values.lastEngagement,
          tagIds: tags,
        };
        await axios.post(
          `${apiUrl}/api/userSource/${entityName}/contract/import`,
          data
        );
      } else if (step === "csvImport") {
        const data = {
          csvFileName: values.csvName,
          csvFileUrl: values.csv,
          tagIds: tags,
          entityName,
        };
        const res = await axios.post(
          `${apiUrl}/api/userSource/${entityName}/csv/import`,
          data
        );
      } else if (step === "singleUserImport") {
        const unsupportedEcosystems = new Set();
        let isValid = true;

        if (values.walletAddress?.trim() && !isAddress(values.walletAddress)) {
          setError("walletAddress", {
            type: "manual",
            message: formatMessage({ id: "global.inputCheck.invalidAddress" }),
          });
          isValid = false;
        } else {
          const detected = getEcosystemByAddress(values.walletAddress);
          if (detected && !enabledEcosystems.includes(detected)) {
            unsupportedEcosystems.add(detected);
            setError("walletAddress", {
              type: "manual",
              message: formatMessage({
                id: "global.inputCheck.unsupportedEcosystem",
              }),
            });
            isValid = false;
          }
        }

        if (unsupportedEcosystems.size > 0) {
          dispatchSnackbar({
            message: `${formatMessage({
              id: "global.snackbar.error.unsupportedEcosystem",
            })}: ${Array.from(unsupportedEcosystems).join(", ")}`,
            variant: "error",
          });
          return;
        }

        if (!isValid) return;

        const data = {
          address: values.walletAddress,
          email: values.email,
          discordHandle: values.discordHandle,
          telegramHandle: values.telegramHandle,
          twitterHandle: values.twitterHandle,
          clientUid: values.clientUid,
          tagIds: tags,
        };

        await axios.post(
          `${apiUrl}/api/userSource/${entityName}/singleUser/import`,
          data
        );
      }

      if (step === "singleUserImport")
        dispatchSnackbar({
          message: formatMessage({ id: "global.snackbar.success.import" }),
          variant: "success",
        });
      handleClose();
      setTimeout(() => {
        getUserList(true);
      }, 2000);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setIsUploading(false);
    }
  };

  async function handlePreviewSmartContract() {
    try {
      setIsUploading(true);

      const { data } = await axios.post(
        apiUrl + `/api/userSource/${entityName}/contract/preview`,
        {
          address: values.address,
          chainId: values.chainId.value,
          type: values.importType.value,
          range: values.lastEngagement,
        }
      );

      setValue("totalCount", data?.total);
      setValue(
        "createdCount",
        parseInt(data?.total) - parseInt(data?.existing)
      );
      setValue("updatedCount", data?.existing);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setIsUploading(false);
    }
  }

  useEffect(() => {
    if (step === "smartContractImport1") {
      if (
        values.lastEngagement &&
        values.chainId &&
        values.contract &&
        values.importType
      ) {
        handlePreviewSmartContract();
      }
    }
  }, [
    values.lastEngagement,
    values.chainId,
    values.contract,
    values.importType,
  ]);

  const importTypes = [
    {
      name: "nft",
      label: "NFT Holders",
      value: "nft-holder",
      id: "nft-holder",
    },
    {
      name: "token",
      label: "Token Holders",
      value: "token-holder",
      id: "token-holder",
    },
    {
      name: "contract",
      label: "Contract Interactors",
      value: "contract-interactor",
      id: "contract-interactor",
    },
  ];

  const importTypesZircuit = [
    {
      name: "contract",
      label: "Contract Interactors",
      value: "contract-interactor",
      id: "contract-interactor",
    },
  ];

  const [openAddressSearch, setOpenAddressSearch] = useState(false);
  const [importContract, setImportContract] = useState({});
  const dropdownRef = useRef(null);

  const findContract = useCallback(async () => {
    const { address, chainId, importType } = values;
    if (!address || address.length < 20 || !chainId)
      return setOpenAddressSearch(false);
    if (address === importContract?.address) return setOpenAddressSearch(true);
    try {
      setOpenAddressSearch(true);
      setImportContract("");
      const { data } = await axios.post(
        apiUrl + `/api/userSource/${entityName}/contract/info`,
        {
          address,
          chainId: chainId.value,
        }
      );
      setImportContract({
        ...data,
        icon: data.icon || getHexImg(data.address),
        type: importType,
      });
    } catch (err) {
      setOpenAddressSearch(false);
      dispatchSnackbar({ err });
    }
  }, [values, importContract?.address]);

  const disabledModalFooterSteps = ["init", "webhook"];

  return (
    <PureModal
      open={open}
      closeBtn={closeBtn}
      onClose={handleClose}
      sx={{ zIndex: 10000, borderRadius: "4px" }}
      maxWidth={step === "init" ? "848px" : "640px"}
      {...props}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(handleImport)}
          onKeyDown={(e) => {
            // 阻止 Enter 鍵提交表單，除非是在按鈕上
            if (e.key === "Enter" && e.target.type !== "submit") {
              e.preventDefault();
            }
          }}
        >
          <Box
            p={step === "init" ? "24px 24px 24px 32px" : "24px"}
            borderBottom="1px solid #E6E6E6"
            fontSize={20}
            fontWeight={700}
            className="item-flex"
            justifyContent="space-between"
          >
            <Box className="item-flex" color="#555">
              <Header />
            </Box>
            <i
              className="meta-crm-icon-ic_cancel font-size-20"
              onClick={handleClose}
              style={{ cursor: "pointer" }}
            />
          </Box>
          <Box
            p={
              step === "init"
                ? "24px 32px 32px"
                : step === "smartContractImport1"
                ? "0"
                : "16px 32px 24px"
            }
            bgcolor={step === "init" ? "#FAFAFD" : "#fff"}
            borderRadius={
              disabledModalFooterSteps.includes(step) ? "0 0 4px 4px" : 0
            }
            maxHeight="540px"
            overflow="auto"
          >
            {step === "init" && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Box>
                    <StyledImportCard mb="16px">
                      <Dropzone
                        onDrop={(acceptedFiles) =>
                          handleChooseFile(acceptedFiles[0])
                        }
                      >
                        {({ getRootProps, getInputProps }) => (
                          <StyledImportCard
                            sx={{ cursor: "pointer", padding: 0 }}
                            {...getRootProps()}
                          >
                            <input
                              type="file"
                              {...getInputProps()}
                              accept=".csv"
                              placeholder="Select a file"
                            />
                            <StyledImportIcon>
                              <img
                                src={
                                  require("assets/img/cdp_import_ic/csv.svg")
                                    .default
                                }
                              />
                            </StyledImportIcon>
                            <Box
                              color="#222"
                              fontWeight={700}
                              mt="16px"
                              mb="4px"
                            >
                              {formatMessage({
                                id: "popup.cdpImport.csvUpload",
                              })}
                            </Box>
                            <Box color="#555" fontWeight={400}>
                              {formatMessage({
                                id: "popup.cdpImport.csvUpload.description",
                              })}
                            </Box>
                          </StyledImportCard>
                        )}
                      </Dropzone>

                      <Box
                        height="1px"
                        width="100%"
                        my="16px"
                        bgcolor="#E6E6E6"
                      />
                      <Box
                        sx={{
                          width: "100%",
                          textAlign: "center",
                          color: theme.customColors.grey[700],
                        }}
                      >
                        <Box mb={1}>
                          {formatMessage({
                            id: "popup.cdpImport.needTemplate",
                          })}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "4px",
                            fontSize: "12px",
                          }}
                        >
                          {formatMessage(
                            {
                              id: "popup.cdpImport.downloadTemplate.description",
                            },
                            {
                              link: (
                                <Box
                                  color={theme.customColors.purple[500]}
                                  fontWeight={700}
                                  sx={{ cursor: "pointer" }}
                                  onClick={() =>
                                    window.open(
                                      "https://storage.googleapis.com/metacrm-assets/csv/MetaCRM_CSV_template_v4.csv",
                                      "_blank"
                                    )
                                  }
                                >
                                  {formatMessage({
                                    id: "popup.cdpImport.downloadTemplate",
                                  })}
                                </Box>
                              ),
                            }
                          )}
                        </Box>
                      </Box>
                    </StyledImportCard>

                    <Box className="item-flex" gap="16px">
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Tooltip
                            title={formatMessage({
                              id: "global.tooltip.onlyEvmEcosystem",
                            })}
                          >
                            <Box sx={{ height: "100%" }}>
                              <StyledImportCard
                                sx={{
                                  cursor: fetchFromSmartContractDisabled
                                    ? "not-allowed"
                                    : "pointer",
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                                onClick={() => {
                                  setStep("smartContractImport1");
                                  setValue("lastEngagement", [
                                    moment()
                                      .utc()
                                      .startOf("day")
                                      .subtract(1, "month")
                                      .valueOf(),
                                    moment().utc().endOf("day").valueOf(),
                                  ]);
                                }}
                                className={
                                  fetchFromSmartContractDisabled
                                    ? "disabled-component"
                                    : "cursor-pointer"
                                }
                              >
                                <StyledImportIcon>
                                  <img
                                    src={
                                      require("assets/img/cdp_import_ic/smartContract.svg")
                                        .default
                                    }
                                  />
                                </StyledImportIcon>
                                <Box
                                  color="#222"
                                  fontWeight={700}
                                  mt="16px"
                                  textAlign="center"
                                >
                                  {formatMessage({
                                    id: "popup.cdpImport.fetchByContract",
                                  })}
                                </Box>
                              </StyledImportCard>
                            </Box>
                          </Tooltip>
                        </Grid>

                        <Grid item xs={4}>
                          <StyledImportCard
                            sx={{
                              cursor: "pointer",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                            onClick={() => setStep("singleUserImport")}
                          >
                            <StyledImportIcon>
                              <img
                                src={
                                  require("assets/img/cdp_import_ic/user.svg")
                                    .default
                                }
                              />
                            </StyledImportIcon>
                            <Box
                              color="#222"
                              fontWeight={700}
                              mt="16px"
                              textAlign="center"
                            >
                              {formatMessage({
                                id: "popup.cdpImport.addSingleUser",
                              })}
                            </Box>
                          </StyledImportCard>
                        </Grid>

                        <Grid item xs={4}>
                          <StyledImportCard
                            sx={{
                              cursor: "pointer",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                            onClick={() => setStep("webhook")}
                          >
                            <StyledImportIcon>
                              <img
                                src={
                                  require("assets/img/cdp_import_ic/webhook.svg")
                                    .default
                                }
                              />
                            </StyledImportIcon>
                            <Box
                              color="#222"
                              fontWeight={700}
                              mt="16px"
                              textAlign="center"
                            >
                              {formatMessage({ id: "popup.cdpImport.webhook" })}
                            </Box>
                          </StyledImportCard>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: { xs: "row", md: "column" },
                      gap: 2,
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: { xs: 1, md: "auto" },
                        width: { xs: "calc(50% - 8px)", md: "100%" },
                      }}
                    >
                      <StyledImportCard
                        mb={{ xs: 0, md: "16px" }}
                        sx={{
                          padding: "24px",
                          height: "100%",
                        }}
                      >
                        <Dropzone
                          onDrop={(acceptedFiles) =>
                            handleChooseFile(acceptedFiles[0])
                          }
                        >
                          {({ getRootProps, getInputProps }) => (
                            <StyledImportCard
                              sx={{ cursor: "pointer", padding: 0 }}
                              {...getRootProps()}
                            >
                              <input
                                type="file"
                                {...getInputProps()}
                                accept=".csv"
                                placeholder="Select a file"
                              />
                              <img src={walletDynamic} alt="Dynamic" />
                              <Box
                                color="#222"
                                fontWeight={700}
                                mt="16px"
                                mb="4px"
                              >
                                {formatMessage({
                                  id: "global.commonWord.dynamic",
                                })}
                              </Box>
                              <Box color="#555" fontWeight={400}>
                                {formatMessage({
                                  id: "global.cdpImport.csvImportDescription",
                                })}{" "}
                                {formatMessage({
                                  id: "global.commonWord.dynamic",
                                })}
                              </Box>
                            </StyledImportCard>
                          )}
                        </Dropzone>
                      </StyledImportCard>
                    </Box>

                    <Box
                      sx={{
                        flex: { xs: 1, md: "auto" },
                        width: { xs: "calc(50% - 8px)", md: "100%" },
                      }}
                    >
                      <StyledImportCard
                        mb={{ xs: 0, md: "16px" }}
                        sx={{
                          padding: "24px",
                          height: "100%",
                        }}
                      >
                        <Dropzone
                          onDrop={(acceptedFiles) =>
                            handleChooseFile(acceptedFiles[0])
                          }
                        >
                          {({ getRootProps, getInputProps }) => (
                            <StyledImportCard
                              sx={{ cursor: "pointer", padding: 0 }}
                              {...getRootProps()}
                            >
                              <input
                                type="file"
                                {...getInputProps()}
                                accept=".csv"
                                placeholder="Select a file"
                              />
                              <Box
                                component="img"
                                src={walletPrivy}
                                alt="Privy"
                              />
                              <Box
                                color="#222"
                                fontWeight={700}
                                mt="16px"
                                mb="4px"
                              >
                                {formatMessage({
                                  id: "global.commonWord.privy",
                                })}
                              </Box>
                              <Box color="#555" fontWeight={400}>
                                {formatMessage({
                                  id: "global.cdpImport.csvImportDescription",
                                })}{" "}
                                {formatMessage({
                                  id: "global.commonWord.privy",
                                })}
                              </Box>
                            </StyledImportCard>
                          )}
                        </Dropzone>
                      </StyledImportCard>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}
            {(step === "csvImport" || step === "smartContractImport2") && (
              <>
                {isUploading ? (
                  <LoadingComponent />
                ) : (
                  <>
                    <Box
                      p="16px"
                      bgcolor="#FAF6FF"
                      color="#4C4C4C"
                      fontWeight={400}
                      borderRadius="4px"
                      display="flex"
                      alignItems="center"
                      mb="24px"
                    >
                      <i
                        className="meta-crm-icon-ic-dataInfoAlert font-size-32"
                        style={{ color: "#7B61FF" }}
                      />
                      <Box ml="12px">
                        <Box color="#4C4C4C" fontWeight={700} mb="8px">
                          {step === "csvImport"
                            ? formatMessage(
                                {
                                  id: "global.file.uploadSuccess",
                                },
                                {
                                  fileName: values.csvName,
                                  rowCount: values.totalCount,
                                }
                              )
                            : formatMessage({
                                id: "global.contract.successfullyDetected",
                              })}{" "}
                        </Box>
                        <Box>
                          <div>
                            {formatMessage(
                              {
                                id: "global.file.uploadSummary",
                              },
                              {
                                createdCount: (
                                  <StyledCountText>
                                    {values?.createdCount?.toLocaleString()}
                                  </StyledCountText>
                                ),
                                updatedCount: (
                                  <StyledCountText>
                                    {values?.updatedCount?.toLocaleString()}
                                  </StyledCountText>
                                ),
                              }
                            )}
                          </div>
                          {values.validation?.totalInvalidAddresses > 0 && (
                            <div>
                              {formatMessage(
                                {
                                  id: "global.file.uploadSummary.invalidAddresses",
                                },
                                {
                                  rowCount: (
                                    <StyledCountText>
                                      {values.validation?.totalInvalidAddresses}
                                    </StyledCountText>
                                  ),
                                }
                              )}
                            </div>
                          )}
                          {values.validation?.totalInvalidEmails > 0 && (
                            <div>
                              {formatMessage(
                                {
                                  id: "global.file.uploadSummary.invalidEmails",
                                },
                                {
                                  rowCount: (
                                    <StyledCountText>
                                      {values.validation?.totalInvalidEmails}
                                    </StyledCountText>
                                  ),
                                }
                              )}
                            </div>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Box color="#555" fontSize={16} fontWeight={700} mb="8px">
                      {formatMessage({ id: "global.dropdown.addTags" })}
                    </Box>
                    <Controller
                      name="tags"
                      control={control}
                      render={({ field }) => (
                        <UserTagComponent
                          value={field.value}
                          handleAddTag={field.onChange}
                        />
                      )}
                    />
                  </>
                )}
              </>
            )}
            {step === "smartContractImport1" && (
              <Box display="flex">
                <Box width="360px" p="16px 32px 24px">
                  <Box color="#555" fontSize={16} fontWeight={700} mb="8px">
                    {formatMessage({ id: "global.contract.blockchain" })}{" "}
                    <span className="required">*</span>
                  </Box>
                  <Controller
                    name="chainId"
                    control={control}
                    rules={{
                      required: formatMessage({
                        id: "global.error.requiredField",
                      }),
                    }}
                    render={({ field, fieldState }) => (
                      <AutoCompleteDropdown
                        disabled={isUploading}
                        value={field.value}
                        onChange={(e, v) =>
                          field.onChange({ label: v.name, value: v.value })
                        }
                        options={evmChainList}
                        sx={{ mb: "24px" }}
                        placeholder={formatMessage({
                          id: "menu.placeholder.select",
                        })}
                        errors={fieldState.error?.message}
                        errorTextAbsolute
                      />
                    )}
                  />
                  <Box
                    color="#555"
                    fontSize={16}
                    fontWeight={700}
                    mt="24px"
                    mb="8px"
                  >
                    {formatMessage({
                      id: "global.contract.address",
                    })}{" "}
                    <span className="required">*</span>
                  </Box>
                  {values.contract ? (
                    <div>
                      <Box
                        bgcolor="#FAFAFD"
                        borderRadius="4px"
                        p="8px"
                        className="item-flex"
                        justifyContent="space-between"
                      >
                        <Box className="item-flex">
                          <Box
                            component="img"
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              mr: "8px",
                            }}
                            src={
                              _.get(values, "icon") ||
                              getHexImg(_.get(values, "address"))
                            }
                          />
                          <Box color="#555">
                            <Box fontWeight={600}>{values.contract.name}</Box>
                            <Box fontSize={12}>
                              {getShortAddress(values.contract.address)}
                            </Box>
                          </Box>
                        </Box>
                        <IconButton
                          onClick={() => {
                            setValue("contract", null);
                            setValue("address", "");
                            setOpenAddressSearch(false);
                            setImportContract("");
                          }}
                          size={16}
                          color="#A5A5A5"
                        >
                          <i className="meta-crm-icon-ic_trash font-size-16" />
                        </IconButton>
                      </Box>
                    </div>
                  ) : (
                    <Box style={{ position: "relative" }}>
                      <Controller
                        name="address"
                        control={control}
                        rules={{
                          required: formatMessage({
                            id: "global.error.requiredField",
                          }),
                        }}
                        render={({ field, fieldState }) => (
                          <InputField
                            {...field}
                            disabled={isUploading}
                            startAdornment={
                              <i className="meta-crm-icon-ic_search font-size-14" />
                            }
                            placeholder={formatMessage({
                              id: "popup.addNewToken.search.placeholder",
                            })}
                            onBlur={findContract}
                            onEnter={findContract}
                            onChange={(e) => {
                              const address = e.target.value;
                              if (!address || address.length < 20)
                                setOpenAddressSearch(false);
                              if (importContract?.address !== address)
                                setOpenAddressSearch(false);
                              field.onChange(address);
                            }}
                            errors={fieldState.error?.message}
                            errorTextAbsolute
                          />
                        )}
                      />
                      {openAddressSearch && (
                        <StyledSearchBar ref={dropdownRef}>
                          {importContract ? (
                            <div
                              className="startRow"
                              onClick={() =>
                                setValue("contract", importContract)
                              }
                            >
                              <img
                                src={importContract.icon}
                                className="avatar"
                              />
                              <div>
                                <div>{importContract.name}</div>
                                <div className="memo">
                                  {getShortAddress(importContract.address)}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div style={{ padding: 5, textAlign: "center" }}>
                              <LoadingComponent />
                            </div>
                          )}
                        </StyledSearchBar>
                      )}
                    </Box>
                  )}
                  <Box
                    color="#555"
                    fontSize={16}
                    fontWeight={700}
                    mt="24px"
                    mb="8px"
                  >
                    {formatMessage({
                      id: "importByContract.labelName.importType",
                    })}
                    <span className="required">*</span>
                  </Box>
                  <Controller
                    name="importType"
                    control={control}
                    rules={{
                      required: formatMessage({
                        id: "global.error.requiredField",
                      }),
                    }}
                    render={({ field, fieldState }) => (
                      <AutoCompleteDropdown
                        disabled={isUploading}
                        value={field.value}
                        onChange={(e, v) => field.onChange(v)}
                        options={
                          values.chainId?.value !== 48900
                            ? importTypes
                            : importTypesZircuit
                        }
                        sx={{ mb: "24px" }}
                        placeholder={formatMessage({
                          id: "menu.placeholder.select",
                        })}
                        errors={fieldState.error?.message}
                        errorTextAbsolute
                      />
                    )}
                  />
                  {values.chainId?.value !== 48900 && ( // customize zircuit
                    <>
                      <Box
                        color="#555"
                        fontSize={16}
                        fontWeight={700}
                        mt="24px"
                        mb="8px"
                      >
                        {formatMessage({ id: "global.contract.timeRange" })}
                      </Box>
                      <Controller
                        name="lastEngagement"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            disabled={isUploading}
                            dateRange={field.value}
                            setDateRange={(v) => field.onChange(v)}
                            sx={{ mb: "24px" }}
                            quickSelectOptions={[
                              {
                                days: 1,
                                label: formatMessage({
                                  id: "datePicker.range.day",
                                }),
                              },
                              {
                                days: 7,
                                label: formatMessage({
                                  id: "datePicker.range.week",
                                }),
                              },
                              {
                                days: 30,
                                label: formatMessage({
                                  id: "datePicker.range.month",
                                }),
                              },
                              {
                                days: "all",
                                label: formatMessage({
                                  id: "datePicker.range.all",
                                }),
                              },
                              {
                                days: "custom",
                                label: formatMessage({
                                  id: "datePicker.range.custom",
                                }),
                              },
                            ]}
                          />
                        )}
                      />
                    </>
                  )}
                </Box>
                <Box
                  width="280px"
                  p="16px 32px 16px 24px"
                  color="#555"
                  bgcolor="#FAFAFD"
                >
                  {isUploading ? (
                    <LoadingComponent />
                  ) : (
                    <>
                      <Box fontSize={16} fontWeight={700} mb="16px">
                        {formatMessage({
                          id: "global.contract.detectedUsersOverview",
                        })}
                      </Box>
                      <Box fontSize={14} mb="16px">
                        {formatMessage({
                          id: "global.contract.detectedUsersDescription",
                        })}
                      </Box>
                      <Box fontSize={16} fontWeight={400}>
                        <span style={{ fontWeight: 700, color: "#7B61FF" }}>
                          {values?.totalCount?.toLocaleString() || "-"}
                        </span>{" "}
                        {formatMessage({ id: "global.commonWord.users" })}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}
            {step === "singleUserImport" && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "24px" }}
              >
                <Controller
                  name="walletAddress"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputField
                      {...field}
                      title={formatMessage({
                        id: "websiteTraffic.popup.columnName.address",
                      })}
                      errors={fieldState.error?.message}
                      errorTextAbsolute
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      title={formatMessage({
                        id: "global.commonWord.emailAddress",
                      })}
                    />
                  )}
                />
                <Controller
                  name="discordHandle"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      title={formatMessage({
                        id: "global.commonWord.discordId",
                      })}
                    />
                  )}
                />
                <Controller
                  name="telegramHandle"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      title={formatMessage({ id: "global.field.telegramId" })}
                    />
                  )}
                />
                <Controller
                  name="twitterHandle"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      title={formatMessage({ id: "global.field.xAccount" })}
                    />
                  )}
                />
                <Controller
                  name="clientUid"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      title={formatMessage({ id: "global.field.clientUid" })}
                    />
                  )}
                />
                <Box>
                  <Box color="#555" fontSize={14} mb="4px">
                    {formatMessage({ id: "global.dropdown.addTags" })}
                  </Box>
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field }) => (
                      <UserTagComponent
                        value={field.value}
                        handleAddTag={field.onChange}
                      />
                    )}
                  />
                </Box>
              </Box>
            )}

            {step === "webhook" && <WebhookImport />}
          </Box>
          {!disabledModalFooterSteps.includes(step) && (
            <Box
              p="16px 24px"
              sx={{ boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.10)" }}
              className="item-flex"
              justifyContent="flex-end"
              gap="12px"
            >
              <Button
                onClick={handleCancel}
                variant="outlined"
                disabled={isUploading}
              >
                {formatMessage({ id: "global.btn.cancel" })}
              </Button>
              <Button
                onClick={handleImport}
                disabled={
                  isUploading || (step === "singleUserImport" && !isDirty)
                }
              >
                {step === "smartContractImport1"
                  ? formatMessage({ id: "global.btn.next" })
                  : formatMessage({ id: "global.btn.completeImport" })}
              </Button>
            </Box>
          )}
        </form>
      </FormProvider>
    </PureModal>
  );
}
